/* lib/api/index.js — РЕАЛЬНЫЙ фасад данных поверх apiFetch + unwrap + адаптеры.
 * Наполняется по доменам (фазы плана). Методы возвращают форму, совместимую с тем,
 * что ждёт UI прототипа (адаптеры скрывают расхождения backend↔mock), и БРОСАЮТ ошибку
 * по контракту fakeApi (через unwrap) — поэтому useFakeQuery и catch-ветки работают без правок. */
import * as documents from './workspaceDocuments.js';
import * as draftEntities from './draftEntities.js';
import * as kitchen from './kitchen.js';
import * as menus from './menus.js';
import * as pendingChanges from './pendingChanges.js';
import * as pricing from './pricing.js';
import * as skills from './skills.js';
import * as ttk from './ttk.js';
import * as versions from './versions.js';
import { unwrap } from './unwrap.js';
import { fmt } from '../format.js';
import { adaptConfirmAction, adaptDraftEntity, adaptSkillCard } from '../adapters/chats.js';
import { adaptDocQuota } from '../adapters/knowledge.js';
import {
  fromApiCardBrief, fromApiCardDetail, toApiCardPayload,
  priceToApiPayload, categoryLabel,
} from '../adapters/ingredients.js';
import {
  fromApiMenuBrief, fromApiMenuDetail, toMenuMetaPayload,
  dishPickerRow,
} from '../adapters/menus.js';
import { fromApiTTKBrief, fromApiTTKDetail, toTTKPayload } from '../adapters/ttk.js';
import { adaptVersion } from '../adapters/versions.js';

const asArray = (d) => (Array.isArray(d) ? d : (d && d.results) || []);

// ── Сырьё (Ingredients) ───────────────────────────────────────────────
// Реестр фильтрует/сортирует/группирует client-side → отдаём весь набор (бэк-список без пагинации),
// адаптированный в форму карточки реестра {id,name,type_label(RU-категория),current_price_brief}.
export async function listIngredients() {
  const data = await unwrap(kitchen.listCards({}));
  return asArray(data).map(fromApiCardBrief);
}

// Detail редактора = 2 эндпоинта: card (+prices) обязателен, used-in best-effort.
export async function getCardDetail(id) {
  const detail = await unwrap(kitchen.getCard(id));
  const usedIn = await kitchen.getCardUsedIn(id).catch(() => null);
  return fromApiCardDetail(detail, usedIn && usedIn.data);
}

export async function createCardDetail(card) {
  const created = await unwrap(kitchen.createCard(toApiCardPayload(card, { withPrices: true })));
  return fromApiCardDetail(created, null);
}

export async function updateCardDetail(id, card) {
  const updated = await unwrap(kitchen.updateCard(id, toApiCardPayload(card)));
  return fromApiCardDetail(updated, null);
}

export async function deleteCardDetail(id) {
  return unwrap(kitchen.deleteCard(id));
}
// F41 — массовое удаление сырья: возвращает {deleted, skipped} (не unwrap'им
// под ошибку — per-item результат, как у bulkDeleteTTK).
export const bulkDeleteCards = (ids) => kitchen.bulkDeleteCards(ids);

// LLM-оценка КБЖУ на 100 г по имени (+опц. категория) — кнопка «Заполнить через AI»
// в редакторе сырья. → {kcal,protein,fat,carbs,allergens}; бросает по контракту fakeApi.
export async function estimateKbju(name, category) {
  return unwrap(kitchen.estimateKbju(name, category));
}

// Имя сущности Кухни для крошек drawer-стека (дефолт-резолвер KitchenBreadcrumb).
// Лёгкий одиночный GET; ошибки → null (крошка покажет «#id»). 'new'/'new-*' сюда не
// доходят — KitchenBreadcrumb короткозамыкает их статичной подписью.
export async function resolveKitchenEntityName(type, id) {
  try {
    if (type === 'card') { const d = await unwrap(kitchen.getCard(id)); return (d && d.name) || null; }
    if (type === 'ttk') { const d = await unwrap(ttk.getTTK(id)); return (d && d.title) || null; }
    if (type === 'menu') { const d = await unwrap(menus.getMenu(id)); return (d && (d.name || d.title)) || null; }
    return null;
  } catch { return null; }
}

// Поставщик по имени → id (найти без учёта регистра / создать). Пусто → null (цена без поставщика).
async function resolveSupplierId(name) {
  const clean = (name || '').trim();
  if (!clean) return null;
  const list = asArray(await unwrap(pricing.listSuppliers()).catch(() => []));
  const found = list.find((s) => (s.name || '').trim().toLowerCase() === clean.toLowerCase());
  if (found) return found.id;
  const created = await unwrap(pricing.createSupplier({ name: clean })).catch(() => null);
  return created ? created.id : null;
}

// Точечный PATCH веса штуки (форма цены «₽/шт»): только два поля, чтобы не
// сохранять попутно несохранённые правки нутриентов из открытого редактора.
// Без piece_weight_g цена ₽/шт не конвертируется в ₽/г и не доходит до
// себестоимости ТТК — бэк-сигнал после PATCH сам пересчитает цепочку.
export async function setCardPieceWeight(id, grams) {
  return unwrap(kitchen.updateCard(id, {
    default_unit: 'шт', piece_weight_g: Number(grams),
  }));
}

// Закупочные цены существующей карточки — отдельные эндпоинты (PATCH карточки их игнорит).
export async function addCardPrice(cardId, priceInput) {
  const supplier_id = await resolveSupplierId(priceInput.supplier_name);
  const body = { card_id: cardId, ...priceToApiPayload(priceInput) };
  if (supplier_id) body.supplier_id = supplier_id;
  return unwrap(pricing.createPrice(body));
}

export async function deleteCardPrice(priceId) {
  return unwrap(pricing.deletePrice(priceId));
}

// ── ТТК / Рецепты ─────────────────────────────────────────────────────
// Реестр фильтрует/сортирует client-side; kind фильтруем на сервере (вкладка dish/semiproduct).
export async function listTTKBriefs(params = {}) {
  const data = await unwrap(ttk.listTTK(params));
  return asArray(data).map(fromApiTTKBrief);
}

// Detail редактора: getTTK обязателен, used-in (parents/menus) best-effort.
export async function getTTKDetail(id) {
  const detail = await unwrap(ttk.getTTK(id));
  const usedIn = await ttk.getTTKUsedIn(id).then((r) => (r.ok ? r.data : null)).catch(() => null);
  return fromApiTTKDetail(detail, usedIn);
}

// row (UI) → payload TTKIngredientRowWriteSerializer.
function rowToApiPayload(r) {
  const body = {
    name_raw: r.name_raw || '',
    amount_brutto_g: r.amount_brutto_g,
    amount_netto_g: r.amount_netto_g,
    unit: r.unit || 'г',
  };
  if (r.weight_in_dish_g != null) body.weight_in_dish_g = r.weight_in_dish_g;
  if (r.target_kind === 'card' && r.target_id) body.card_id = r.target_id;
  else if (r.target_kind === 'ttk' && r.target_id) body.sub_recipe_id = r.target_id;
  return body;
}

// Создать ТТК: createEmpty (output_g для semiproduct обязателен) → PATCH draft → bulk rows → свежий detail.
export async function createTTKReal(detail) {
  const created = await unwrap(ttk.createEmptyTTK({ title: detail.title, kind: detail.kind, output_g: detail.output_g }));
  const id = created.id;
  await unwrap(ttk.updateTTK(id, toTTKPayload({ ...detail, id, _draft_json: created.draft_json || {} })));
  const rows = (detail.ingredient_rows || []).filter((r) => r && (r.name_raw || r.target_id));
  if (rows.length) await unwrap(ttk.createRowsBulk(id, rows.map(rowToApiPayload)));
  return getTTKDetail(id);
}

// Сохранить скаляры/текст/ГОСТ/аллергены/выход (НЕ строки — те через granular).
export async function saveTTKDraft(id, detail) {
  const updated = await unwrap(ttk.updateTTK(id, toTTKPayload(detail)));
  return fromApiTTKDetail(updated, null);
}

export async function deleteTTKReal(id) { return unwrap(ttk.deleteTTK(id)); }

// Granular row-операции — каждая возвращает свежий TTKDetailSerializer → адаптируем.
export async function ttkCreateRows(id, rows) {
  const d = await unwrap(ttk.createRowsBulk(id, rows.map(rowToApiPayload)));
  return fromApiTTKDetail(d, null);
}
export async function ttkUpdateRow(id, rowId, patch) {
  const body = {};
  if (patch.name_raw !== undefined) body.name_raw = patch.name_raw;
  if (patch.amount_brutto_g !== undefined) body.amount_brutto_g = patch.amount_brutto_g;
  if (patch.amount_netto_g !== undefined) body.amount_netto_g = patch.amount_netto_g;
  if (patch.weight_in_dish_g !== undefined) body.weight_in_dish_g = patch.weight_in_dish_g;
  if (patch.unit !== undefined) body.unit = patch.unit;
  if (patch.notes !== undefined) body.notes = patch.notes;
  const d = await unwrap(ttk.updateRow(id, rowId, body));
  return fromApiTTKDetail(d, null);
}
export async function ttkDeleteRow(id, rowId) {
  const d = await unwrap(ttk.deleteRow(id, rowId));
  return fromApiTTKDetail(d, null);
}
// Перепривязка строки: card_id (или null = отвязка) / sub_recipe_id. Возвращает свежий detail.
export async function ttkRebindRow(id, rowId, entity) {
  let d;
  if (entity && entity.kind === 'ttk') {
    d = await unwrap(ttk.updateRow(id, rowId, { sub_recipe_id: entity.id }));
  } else if (entity && entity.kind === 'card') {
    d = await unwrap(ttk.linkRowToCard(id, rowId, entity.id));
  } else {
    d = await unwrap(ttk.linkRowToCard(id, rowId, null)); // отвязка
  }
  return fromApiTTKDetail(d, null);
}
export async function ttkAiFillMissing(id) {
  const res = await unwrap(ttk.aiFillMissingRows(id));
  return fromApiTTKDetail(res.ttk || res, null);
}

// Пикер состава ТТК: card → реестр сырья; semiproduct → ТТК kind=semiproduct.
// Возвращает [{id, kind:'card'|'ttk', label, group, hint}] (форма listPickerEntities).
export async function pickerFetch(kind, q) {
  if (kind === 'semiproduct') {
    const data = await unwrap(ttk.listTTK({ kind: 'semiproduct', q }));
    return asArray(data).map((t) => ({
      id: t.id, kind: 'ttk', label: t.title, group: 'Полуфабрикаты',
      hint: t.cost_per_portion_rub != null ? `${fmt.money(Number(t.cost_per_portion_rub))}/порц` : 'без расчёта',
    }));
  }
  const data = await unwrap(kitchen.listCards({ q }));
  return asArray(data).map((c) => ({
    id: c.id, kind: 'card', label: c.name, group: categoryLabel(c.category),
    hint: c.current_price_brief ? fmt.money(Number(c.current_price_brief.amount_rub)) : 'без цены',
  }));
}

function rowChanged(o, r) {
  return o.name_raw !== r.name_raw
    || o.amount_brutto_g !== r.amount_brutto_g
    || o.amount_netto_g !== r.amount_netto_g
    || o.weight_in_dish_g !== r.weight_in_dish_g
    || o.unit !== r.unit
    || o.target_kind !== r.target_kind
    || o.target_id !== r.target_id;
}

// Синхронизировать строки (edit-locally → save) для существующей ТТК: delete/update/create-diff.
// originalRows — серверные строки (с id), localRows — текущее состояние редактора.
export async function syncTTKRows(id, originalRows, localRows) {
  const localById = new Map(localRows.filter((r) => r.id != null).map((r) => [r.id, r]));
  const origById = new Map((originalRows || []).map((r) => [r.id, r]));
  // удалённые
  for (const o of originalRows || []) {
    if (o.id != null && !localById.has(o.id)) await unwrap(ttk.deleteRow(id, o.id));
  }
  // изменённые существующие
  for (const r of localRows) {
    if (r.id == null) continue;
    const o = origById.get(r.id);
    if (!o || !rowChanged(o, r)) continue;
    if (r.target_kind == null && o.target_kind != null) {
      await unwrap(ttk.linkRowToCard(id, r.id, null)); // отвязка
    }
    await unwrap(ttk.updateRow(id, r.id, rowToApiPayload(r)));
  }
  // новые
  const newRows = localRows.filter((r) => r.id == null && (r.name_raw || r.target_id));
  if (newRows.length) await unwrap(ttk.createRowsBulk(id, newRows.map(rowToApiPayload)));
  return getTTKDetail(id);
}
export { buildExportUrl as ttkExportUrl } from './ttk.js';
export { bulkDeleteTTK, buildBulkExportUrl as ttkBulkExportUrl } from './ttk.js';
export { downloadFile } from './client.js';

// ── Меню (Menus) ──────────────────────────────────────────────────────
// Реестр фильтрует/сортирует/ищет client-side → отдаём весь набор, адаптированный в brief.
export async function listMenuBriefs(params = {}) {
  const data = await unwrap(menus.listMenus(params));
  return asArray(data).map(fromApiMenuBrief);
}

export async function getMenuDetailReal(id) {
  const d = await unwrap(menus.getMenu(id));
  return fromApiMenuDetail(d);
}

export async function deleteMenuReal(id, opts) { return unwrap(menus.deleteMenu(id, opts)); }
// F41 — массовое удаление меню: {deleted, deleted_dishes, skipped} (per-item).
export const bulkDeleteMenus = (ids, opts) => menus.bulkDeleteMenus(ids, opts);

// Дублирование → MenuDetailSerializer копии (новый id) → редактор делает push('menu', copy.id).
export async function duplicateMenuReal(id) {
  const d = await unwrap(menus.duplicateMenu(id));
  return fromApiMenuDetail(d);
}

// Пикер «Добавить блюда из ТТК»: список ТТК kind=dish → строки пикера (цена из ТТК).
export async function menuDishPicker(q) {
  const data = await unwrap(ttk.listTTK({ kind: 'dish', q, sort: '-updated' }));
  return asArray(data).map(dishPickerRow);
}

const dishTtkIds = (dishes) => (dishes || []).map((d) => d.ttk_id).filter((id) => typeof id === 'number');

// Секции: синхронизировать локальные (edit-locally) с серверными — create/update/delete + reorder.
// Возвращает Map localSecId(temp-string | int) → realSecId(int) для маппинга dish.section_id.
async function syncMenuSections(menuId, originalSections, localSections) {
  const secMap = new Map();
  const localIds = new Set((localSections || []).map((s) => s.id));
  for (const o of originalSections || []) {
    if (!localIds.has(o.id)) await unwrap(menus.deleteSection(menuId, o.id));
  }
  for (const s of localSections || []) {
    const isNewSec = typeof s.id !== 'number';
    const body = { title: (s.title || 'Раздел').trim(), subtitle: s.subtitle || '' };
    if (isNewSec) {
      const created = await unwrap(menus.createSection(menuId, body));
      secMap.set(s.id, created.id);
    } else {
      secMap.set(s.id, s.id);
      const o = (originalSections || []).find((x) => x.id === s.id);
      if (o && (o.title !== s.title || (o.subtitle || '') !== (s.subtitle || ''))) {
        await unwrap(menus.updateSection(menuId, s.id, body));
      }
    }
  }
  if ((localSections || []).length) {
    const order = localSections.map((s) => secMap.get(s.id)).filter((x) => x != null);
    if (order.length) await unwrap(menus.reorderSections(menuId, order));
  }
  return secMap;
}

// Полное per-op сохранение меню (edit-locally → один Save, блоб-save несовместим с бэком):
//   isNew → POST createMenu(мета+стиль+stub_positions+членство) ;
//   секции (create/update/delete/reorder, temp→real id) ;
//   членство+мета+стиль PATCH (dishes.set — для существующего; сохраняет раскладку оставшихся) ;
//   раскладка reorder (section/order/in_stop_list, плотный 0-based порядок в разделе).
// original — серверный detail (для диффа секций), local — текущее состояние редактора, stubs — черновые блюда нового меню.
export async function saveMenuFull(id, original, local, stubs) {
  const isNew = id == null;
  let menuId = id;
  let orig = original;
  if (isNew) {
    const body = toMenuMetaPayload(local);
    if (Array.isArray(stubs) && stubs.length) body.stub_positions = stubs.map((name) => ({ name }));
    const ids = dishTtkIds(local.dishes);
    if (ids.length) body.dishes = ids.map((tid) => ({ ttk_id: tid }));
    const created = await unwrap(menus.createMenu(body));
    menuId = created.id;
    orig = fromApiMenuDetail(created);
  }
  const secMap = await syncMenuSections(menuId, (orig && orig.sections) || [], local.sections || []);
  // Членство переустанавливаем только для существующего меню (для нового — задано в createMenu,
  // иначе dishes.set затрёт стаб-блюда из stub_positions). Мета/стиль нового меню уже в createMenu.
  if (!isNew) {
    const meta = toMenuMetaPayload(local);
    meta.dishes = dishTtkIds(local.dishes).map((tid) => ({ ttk_id: tid }));
    await unwrap(menus.updateMenu(menuId, meta));
  }
  const layoutDishes = (local.dishes || []).filter((d) => typeof d.ttk_id === 'number');
  if (layoutDishes.length) {
    const counters = {};
    const layout = layoutDishes.map((d) => {
      const realSid = d.section_id != null ? (secMap.get(d.section_id) ?? null) : null;
      const key = realSid == null ? 'none' : realSid;
      counters[key] = (counters[key] ?? -1) + 1;
      return { ttk_id: d.ttk_id, section_id: realSid, order: counters[key], in_stop_list: !!d.in_stop_list };
    });
    await unwrap(menus.reorderMenu(menuId, layout));
  }
  return getMenuDetailReal(menuId);
}

export { getMenuPrintUrl as menuPrintUrl } from './menus.js';

// ── База знаний (Knowledge / Documents) ───────────────────────────────
// GET /api/workspace-documents/ → {documents, quota}. Документ 1:1 с DocumentCard (без адаптации);
// квота нормализуется (used_mb Decimal→Number, лимит -1 → null = «без лимита»).
// Опц. workspaceId — скоуп на бизнес-ws из настроек (X-Workspace-Id без переключения).
export async function listDocumentsReal(workspaceId) {
  const data = await unwrap(documents.listDocuments(workspaceId));
  return {
    documents: (data && data.documents) || [],
    quota: adaptDocQuota(data && data.quota),
  };
}
// Мутации/загрузка/стрим — RAW {ok,status,data}: экран
// ветвится по res.ok. Загрузка → 402 (code='count_exceeded'/'storage_exceeded'/'file_too_large'/'feature_disabled')
// или 400 (code='unsupported_type'/'no_file'); PATCH → 400 (code='bad_kind'/'bad_summary'); summarize → SSE-кадры
// delta/status/done/error (+ 400 code='unreadable_file' до стрима).
export {
  createDocument as createDocumentReal,
  updateDocument as updateDocumentReal,
  deleteDocument as deleteDocumentReal,
  streamSummary as streamDocumentSummary,
} from './workspaceDocuments.js';

// ── Настройки: Профиль / Безопасность / Команда ───────────────────────────
// Адаптеры держат форму под экраны (profileReal/teamReal) и throw-контракт (unwrap):
//   profileReal — user-level (PATCH имя, смена/отмена email, смена пароля, выход со всех, удаление);
//   teamReal    — workspace-scoped (getTeam/invite/update/revoke member + revoke invitation), wsId — явный
//                 первый аргумент (TeamTab берёт activeWorkspace.id из useAuth).
export { profileReal, teamReal } from '../adapters/settings.js';

// ── Auth: NON-session флоу (регистрация / сброс / верификация / приглашение) ──
// Сессия login/logout/me — в AuthProvider (useAuth), не здесь. authReal держит форму под
// экраны auth/* и throw-контракт (unwrap). Маппинги (register name→full_name,
// confirmPasswordReset password→new_password, confirmEmailChange только {uid,token},
// preview pass-through; email_matches деривит сам InviteAccept против useAuth().user.email)
// — см. adapters/auth.js.
export { authReal } from '../adapters/auth.js';

// ── Версии / снимки (Versions) ────────────────────────────────────────
// Кросс-каттинг: один VersionsBlock на 3 редактора. kind ∈ {ttk, card, menu} — 1:1 с тем, что
// передают редакторы (IngredientEditor kind="card", TTKEditor kind="ttk", MenuEditor kind="menu")
// и тем, как бэк определяет subject по URL (versioning.urls.make_version_urls(kind)).
const VERSIONS_FNS = {
  ttk: { list: versions.listTTKVersions, get: versions.getTTKVersion, restore: versions.restoreTTKVersion, compare: versions.compareTTKVersions },
  card: { list: versions.listCardVersions, get: versions.getCardVersion, restore: versions.restoreCardVersion, compare: versions.compareCardVersions },
  menu: { list: versions.listMenuVersions, get: versions.getMenuVersion, restore: versions.restoreMenuVersion, compare: versions.compareMenuVersions },
};
const versionFns = (kind) => VERSIONS_FNS[kind] || VERSIONS_FNS.ttk;

// Список версий со снимками одним запросом: list-клиенты зовут ?include=snapshot, поэтому
// каждая version уже содержит snapshot (VersionsBlock/VersionCompareModal/SnapshotView читают
// version.snapshot напрямую). adaptVersion null-безопасен, если снимок вдруг отсутствует.
// Контракт совместим с fakeApi.getVersions → плоский массив версий.
export async function listVersions(kind, id) {
  const data = await unwrap(versionFns(kind).list(id));
  const metas = (data && data.versions) || [];
  return metas.map((m) => adaptVersion(kind, m));
}

// Одна версия (detail со snapshot) — на случай прямого &v-deep-link без полного списка.
export async function getVersion(kind, id, versionNumber) {
  const data = await unwrap(versionFns(kind).get(id, versionNumber));
  return adaptVersion(kind, data && data.version);
}

// Восстановление: POST с пустым телом → 201 {restored_from, new_version, report}. UI после
// успеха перезапрашивает список (новая версия добавляется в хвост). Возвращаем сырой ответ —
// VersionsBlock смотрит только на «успех/throw», а список тянет заново через query.reload().
export async function restoreVersion(kind, id, versionNumber) {
  return unwrap(versionFns(kind).restore(id, versionNumber));
}

// Серверный compare (a vs b; b пусто → vs текущее состояние). VersionCompareModal сейчас диффит
// snapshot'ы client-side (DiffView над a.snapshot/b.snapshot из уже загруженного списка), поэтому
// в текущем UI не вызывается. Оставляем в фасаде для паритета клиента и будущего серверного диффа.
export async function compareVersions(kind, id, a, b) {
  return unwrap(versionFns(kind).compare(id, a, b));
}

// ── Чат: структурные кадры (chat-2) ───────────────────────────────────
// Поверх stateless-эндпойнтов inline-черновиков и подготовленных правок. Все методы
// через unwrap() → бросают err{status,code,detail} по контракту fakeApi (карточки feed
// ловят err.code в catch-ветках, как раньше у fakeApi.recomputeDraft/commitDraft/…).

// recompute: пересчёт derived черновика БЕЗ записи. {computed, warnings}; computed только
// для ttk ({cost_per_portion_rub, food_cost_pct, kcal_per_portion, …}, Decimal-строки →
// Number через адаптер), для card/menu — null. Карточка берёт computed (объект чисел).
export async function recomputeDraft(kind, payload, edited) {
  const data = await unwrap(draftEntities.recomputeDraft(kind, payload, edited));
  // Стрим присылает computed как часть кадра draft_entity — там же его форма; здесь
  // переиспользуем тот же коэрс через adaptDraftEntity({computed}).computed.
  const adapted = adaptDraftEntity({ computed: data && data.computed });
  return { computed: adapted.computed, warnings: (data && data.warnings) || [] };
}

// commit: создать реальную сущность из payload. {committed, kind, id, title, editor_route,
// bound_recipes?} | 400 {error:'duplicate'|'invalid', code, detail} (unwrap → throw{code}).
// Карточка ждёт {url} для «Открыть в редакторе» → отдаём editor_route как url + сам объект.
export async function commitDraft({ messageId, proposalId, kind, payload, chatId }) {
  const data = await unwrap(
    draftEntities.commitDraft({ messageId, proposalId, kind, payload, chatId }),
  );
  return {
    ...data,
    url: data.editor_route || null,
    bound_recipes: data.bound_recipes || [],
  };
}

// F40 W5: фоновая сборка дерева детей (tree-job; живёт на беке, переживает refresh).
// unwrap → {status, active, total, done, failed, cancel_requested, job_id?,
//           root_entity_kind?, root_entity_id?, nodes:[…]}.
export async function treeBuildStatus(jobId) {
  return unwrap(ttk.getTreeBuildStatus(jobId));
}
export async function treeBuildByRoot(rootKind, rootId) {
  return unwrap(ttk.getTreeBuildByRoot(rootKind, rootId));
}
export async function treeBuildStart(rootKind, rootId, ctx = {}) {
  return unwrap(ttk.startTreeBuild(rootKind, rootId, ctx));
}
export async function treeBuildCancel(jobId) {
  return unwrap(ttk.cancelTreeBuild(jobId));
}
export async function treeBuildResume(jobId) {
  return unwrap(ttk.resumeTreeBuild(jobId));
}

// commit pending-change: применить подготовленную правку. 200 {committed, summary,
// restore_version_id, restore_version_number, target:{kind,id}, …} | 409 {error:'stale'|
// 'expired'|'gone'} | 404 | 400 {error:'invalid'}. unwrap прокидывает err.error/err.status
// → карточка (catch) различает stale-ветку по err.error/err.status===409.
export async function commitPendingChange(id) {
  return unwrap(pendingChanges.commitPendingChange(id));
}

// cancel pending-change: 200 {cancelled, status} | 409 {error:'committed'} | 404.
export async function cancelPendingChange(id) {
  return unwrap(pendingChanges.cancelPendingChange(id));
}

// restore: откат применённой правки через versioning-REST (version_number, не PK).
export async function restorePendingChange(kind, targetId, versionNumber) {
  return unwrap(pendingChanges.restoreEntityVersion(kind, targetId, versionNumber));
}

// Скиллы для галереи EmptyState + справки чата. Бэк → [{slug,name,hint,starter_hint,
// description,inputs,outputs,examples}]; адаптер → {slug,icon,name,hint,description,
// inputs,outputs,examples,examplePrompt} (карточка + панель «как работать с функцией»).
export async function listSkills() {
  const data = await unwrap(skills.listSkills());
  return (Array.isArray(data) ? data : []).map(adaptSkillCard);
}

// Адаптеры структурных кадров — провайдер крепит их к metadata сообщения «как из истории».
export { adaptDraftEntity, adaptConfirmAction } from '../adapters/chats.js';
